import ToolbarPopover from "../ToolbarPopover/ToolbarPopover";
import { Select, Counter, Text, Flex, Button, ToolIcon } from "@theorgchartdev/storybook";
import { useRef, useState } from "react";
import * as StateLib from "@/Utils/StateLib";
import { ConvertUnitsToPixels, ConvertPixelsToUnits } from "@/Utils/DataLib";
import { UpdateBoxProperty } from "@/Utils/StateLib";
import { getHoverTipContent } from "@/Utils/Misc";
import {
  kSizingModeOptions,
  enumBoxHeightMode,
  enumUpdateProperty,
  kMaxBoxHeight
} from "@coreapp/constants";
import { StringCodeToVSizingMode } from "@coreapp/utils/StringCodeToVSizingMode";
import { VSizingModeToStringCode } from "@coreapp/utils/VSizingModeToStringCode";
import { appstr } from "@coreapp/utils/appstr";

const BuildBoxHeightOptions = updateBoxHeightMode => {
  let heightOptions = [];
  const wsMgr = StateLib.GetWSMgr();
  let optionsList = [kSizingModeOptions[0], kSizingModeOptions[1]];
  if (wsMgr.IsStdBox()) optionsList.push(kSizingModeOptions[2]);
  for (let i of optionsList) {
    let option = {};
    option.label = appstr(i);
    option.dataTestId = `box-height-mode-${i}`;
    option.value = StringCodeToVSizingMode(i);
    option.clickHandler = updateBoxHeightMode;
    heightOptions.push(option);
  }
  return heightOptions;
};

const BoxHeightPopover = ({ layout }) => {
  const openCtrlRef = useRef(null);
  const useMetric = StateLib.UseMetric();

  const currentHeightMode = layout.GetHeightSizingMode() ?? enumBoxHeightMode.fixedheight;
  const [heightMode, setHeightMode] = useState(currentHeightMode);

  const UpdateBoxHeightMode = target => {
    const wsMgr = StateLib.GetWSMgr();
    const newHeightMode = target.value;
    layout.SetHeightSizingMode(newHeightMode);
    setHeightMode(newHeightMode);
    wsMgr.UpdateBoxSizingMode(true);
    StateLib.ReRenderCanvas();
  };

  const ResetBoxHeight = () => {
    const curHeight = layout.GetBoxHeight(false);
    const resetHeight = layout.CalcBoxHeight();
    const delta = resetHeight - curHeight;
    UpdateBoxProperty(enumUpdateProperty.boxHeight, delta);
  };

  const UpdateFixedHeight = newFixedHeight => {
    var pixels = ConvertUnitsToPixels(newFixedHeight);
    if (pixels < 1) pixels = 0;
    if (pixels > kMaxBoxHeight) pixels = kMaxBoxHeight;
    const current = layout.GetBoxHeight();
    const delta = pixels - current;
    UpdateBoxProperty(enumUpdateProperty.boxHeight, delta);
  };

  let currentBoxHeight = layout?.GetBoxHeight();
  if (currentBoxHeight != null)
    currentBoxHeight = ConvertPixelsToUnits(currentBoxHeight, useMetric);

  const showFixedHeight = heightMode === enumBoxHeightMode.fixedheight;

  const inputStyles = { width: "60px" };
  const unitLabel = useMetric ? "cm" : "in";

  const heightTrigger = (
    <ToolIcon
      dataTestId={"box-height-trigger"}
      name={"arrows-up-down-to-line"}
      tooltipProps={{ ...getHoverTipContent("Box height"), referenceOpenCtrlRef: openCtrlRef }}
    />
  );

  return (
    <ToolbarPopover
      openCtrlRef={openCtrlRef}
      icon={"arrows-up-down-to-line"}
      otherPopoverProps={{ offsetOptions: -2, reference: heightTrigger }}
      key={5}
    >
      <Flex direction={"column"} gap={"12px"}>
        <Flex justifyContent={"space-between"} alignItems={"center"}>
          <Text modifier={"medium"} size={"small"}>
            {appstr("Box height")}
          </Text>
          {showFixedHeight && (
            <Button
              dataTestId="box-layout-editor-toolbar-height-reset-btn"
              modifier={"secondary"}
              size={"small"}
              onClick={ResetBoxHeight}
            >
              {appstr("Reset")}
            </Button>
          )}
        </Flex>
        <Flex direction={"column"} gap={"12px"} alignItems={"flex-end"}>
          <Select
            dataTestId={"box-height-mode-select"}
            options={BuildBoxHeightOptions(UpdateBoxHeightMode)}
            label={appstr(VSizingModeToStringCode(heightMode))}
            value={heightMode}
            selectSize={"medium"}
          />
          <Flex alignItems={"center"} justifyContent={"space-between"} gap={"8px"}>
            <Counter
              key={6}
              onChange={UpdateFixedHeight}
              currentValue={parseFloat(currentBoxHeight)}
              defaultValue={parseFloat(currentBoxHeight)}
              inputStyles={inputStyles}
              allowDecimals={true}
              callbackOnInputChange={true}
              inputProps={{ disabled: !showFixedHeight, "data-testid": "box-height-input" }}
              decimalPlaces={3}
              minValue={0.1}
              incrementValue={0.1}
              size={"small"}
            />
            <Text size={"small"} colorModifier={"grey"}>
              {unitLabel}
            </Text>
          </Flex>
        </Flex>
      </Flex>
    </ToolbarPopover>
  );
};

export default BoxHeightPopover;
